import type { DataType } from "@repo/lanes";

import { dateFormatToTruncUnit } from "../types/column-format";
import { resolveColumnFormat } from "../utils/merge-columns-configs";
import { aggregateToSql } from "./helpers/aggregate-to-sql";
import { defaultAggForType } from "./helpers/default-agg-for-type";
import { escapeIdentifier } from "./helpers/escape-identifier";
import type { RelativeWindowOptions } from "./helpers/filter-to-sql";
import { filtersToWhereClause } from "./helpers/filters-to-where-clause";
import {
  AGG_DELIMITER,
  type ColumnsConfig,
  type FilterType,
  type RiftConfig,
} from "./types";

/**
 * Row ceiling applied to an ungrouped rift that sets no explicit limit.
 *
 * A row-level rift is `SELECT *` over the whole run, so without a cap DuckDB
 * hands back an Arrow result the size of the source file — defeating the point
 * of reading that file lazily from disk. Grouped/aggregated rifts are exempt:
 * their results are already small, and capping them would silently drop groups.
 *
 * Sorting stays correct because `ORDER BY` is emitted before `LIMIT`, and all
 * filtering/sorting happens in SQL rather than over the returned rows.
 */
export const DEFAULT_RIFT_ROW_LIMIT = 100_000;

export const rift = (
  tableNameStr: string,
  instructions: RiftConfig | null,
  boardFilters: FilterType[] | null,
  columnDefs?: Record<string, DataType> | null,
  columnsConfig?: ColumnsConfig | null,
  opts?: RelativeWindowOptions,
): string => {
  const hasBoardFilters = boardFilters && boardFilters.length > 0;

  if (!instructions) {
    const defaultLimit = `\nLIMIT ${DEFAULT_RIFT_ROW_LIMIT}`;
    if (hasBoardFilters) {
      const boardWhere = filtersToWhereClause(boardFilters, opts);
      return `WITH _board_filtered AS (\n  SELECT * FROM '${tableNameStr}' WHERE ${boardWhere}\n)\nSELECT * FROM _board_filtered${defaultLimit}`;
    }
    return `SELECT * FROM '${tableNameStr}'${defaultLimit}`;
  }

  const {
    hiddenColumns = [],
    groups = {},
    aggregates = {},
    filters = [],
    sorts = [],
    limit = null,
  } = instructions;

  const hiddenSet = new Set(hiddenColumns);

  let ctePrefix = "";
  let fromClause: string;

  if (hasBoardFilters) {
    const boardWhere = filtersToWhereClause(boardFilters, opts);
    ctePrefix = `WITH _board_filtered AS (\n  SELECT * FROM '${tableNameStr}' WHERE ${boardWhere}\n)\n`;
    fromClause = "_board_filtered";
  } else {
    fromClause = `'${tableNameStr}'`;
  }

  const groupAccessors = Object.entries(groups)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const hasGroups = groupAccessors.length > 0;
  const hasAggregates = Object.values(aggregates).some((ops) => ops?.length);

  const blockWhere = filtersToWhereClause(filters, opts);
  const whereClause = blockWhere ? `\nWHERE ${blockWhere}` : "";

  let selectClause: string;
  let groupByClause = "";
  // Aliases this query actually projects. Only tracked for the aggregate
  // branch, where a column absent from the SELECT is also unbindable in
  // ORDER BY. `null` means every source column stays sortable.
  let outputColumns: Set<string> | null = null;

  if (hasGroups || hasAggregates) {
    const selectParts: string[] = [];
    const groupByExprs: string[] = [];
    const outputAliases = new Set<string>();

    for (const accessor of groupAccessors) {
      // Resolve the format the same way the UI does so an unset DATE column
      // still buckets by its primitive default (day) on first load, instead of
      // grouping on the raw timestamp. A persisted format always takes
      // precedence over the type default.
      const fmt = resolveColumnFormat(
        columnsConfig,
        accessor,
        columnDefs?.[accessor],
      );
      const truncUnit =
        fmt.kind === "DATE" ? dateFormatToTruncUnit(fmt.dateFormat) : null;

      if (truncUnit) {
        const col = escapeIdentifier(accessor);
        const truncExpr = `DATE_TRUNC('${truncUnit}', CAST(${col} AS TIMESTAMP))`;
        // Emit the truncation as TIMESTAMP (no outer CAST AS VARCHAR). Arrow
        // now encodes DATE columns as native temporal types, so DuckDB can
        // group and propagate timestamps directly without losing type fidelity
        // through the rift transform.
        selectParts.push(`${truncExpr} AS ${col}`);
        groupByExprs.push(truncExpr);
      } else {
        selectParts.push(escapeIdentifier(accessor));
        groupByExprs.push(escapeIdentifier(accessor));
      }

      outputAliases.add(accessor);
    }

    const groupSetForAgg = new Set(groupAccessors);
    for (const [accessor, ops] of Object.entries(aggregates)) {
      if (!ops.length) continue;
      if (hiddenSet.has(accessor)) continue;
      // A grouped column is a dimension, not a measure — never aggregate it
      // (also avoids emitting two columns with the same alias).
      if (groupSetForAgg.has(accessor)) continue;
      // Single-op aggregates keep the raw column name so downstream config
      // (chart keys, formats, sorts) stays stable. Only multi-op aggregates
      // need the `col::OP` alias to disambiguate the extra output columns.
      const single = ops.length === 1;
      for (const op of ops) {
        const expr = aggregateToSql(accessor, op);
        if (op === "CONCAT" || single) {
          selectParts.push(`${expr} AS ${escapeIdentifier(accessor)}`);
          outputAliases.add(accessor);
        } else {
          const alias = `${accessor}${AGG_DELIMITER}${op}`;
          selectParts.push(`${expr} AS ${escapeIdentifier(alias)}`);
          outputAliases.add(alias);
        }
      }
    }

    // Auto-aggregate non-grouped, non-explicitly-aggregated columns.
    // Only runs when GROUP BY is active AND caller provided column metadata.
    // Each remaining column gets a type-appropriate default aggregation so the
    // full column set stays visible in the result (e.g. the user grouped by
    // "region" but still wants to see "revenue", "name", "created_at").
    if (columnDefs && hasGroups) {
      const groupSet = new Set(groupAccessors);
      // A column has an explicit aggregate if it appears in aggregates with ≥1 op
      const explicitAggSet = new Set(
        Object.entries(aggregates)
          .filter(([, ops]) => ops.length)
          .map(([k]) => k),
      );

      for (const [accessor, dataType] of Object.entries(columnDefs)) {
        if (groupSet.has(accessor)) continue; // already in GROUP BY clause
        if (explicitAggSet.has(accessor)) continue; // user set explicit aggregate(s)
        if (hiddenSet.has(accessor)) continue; // hidden via columnsConfig

        // Alias = original accessor so column config keys remain stable.
        const expr = aggregateToSql(accessor, defaultAggForType(dataType));
        selectParts.push(`${expr} AS ${escapeIdentifier(accessor)}`);
        outputAliases.add(accessor);
      }
    }

    selectClause = selectParts.length > 0 ? selectParts.join(", ") : "*";
    // The `*` fallback projects everything, so no alias restriction applies.
    if (selectParts.length > 0) outputColumns = outputAliases;

    if (hasGroups) {
      groupByClause = `\nGROUP BY ${groupByExprs.join(", ")}`;
    }
  } else {
    selectClause =
      hiddenColumns.length > 0
        ? `* EXCLUDE (${hiddenColumns.map(escapeIdentifier).join(", ")})`
        : "*";
  }

  // A sort can outlive the column it targets — removing a group or hiding an
  // aggregated column leaves the entry behind. Keeping it would emit an
  // ORDER BY on a column the aggregate SELECT no longer projects, which DuckDB
  // rejects ("column must appear in the GROUP BY clause"). Ordering a
  // groupless aggregate is meaningless anyway, so drop the stale entries.
  const sortableColumns = outputColumns;
  const applicableSorts = sortableColumns
    ? sorts.filter(({ column }) => sortableColumns.has(column))
    : sorts;

  let orderByClause = "";
  if (applicableSorts.length > 0) {
    const parts = applicableSorts.map(({ column, direction }) => {
      const nullsOrder = direction === "ASC" ? "NULLS FIRST" : "NULLS LAST";
      return `${escapeIdentifier(column)} ${direction} ${nullsOrder}`;
    });
    orderByClause = `\nORDER BY ${parts.join(", ")}`;
  }

  // An explicit limit always wins. Otherwise only row-level rifts get the
  // default ceiling — a grouped/aggregated result is already bounded.
  const effectiveLimit =
    limit != null && limit > 0
      ? limit
      : hasGroups || hasAggregates
        ? null
        : DEFAULT_RIFT_ROW_LIMIT;
  const limitClause = effectiveLimit != null ? `\nLIMIT ${effectiveLimit}` : "";

  return `${ctePrefix}SELECT ${selectClause} FROM ${fromClause}${whereClause}${groupByClause}${orderByClause}${limitClause}`;
};
